/**
 * Build the view-as lens target from a user record + the client list.
 *
 * One builder for both callers: the picker (ViewAsCard) when a lens opens, and
 * AuthContext when it re-reads the target LIVE (on open and on every reload).
 * The worker already swaps role/clientId/zoneIds from the target's live record
 * on every lensed request; the console's half of the lens (banner, nav, gates,
 * branding, per-user hidden screens) must come from the same live record, or
 * the two halves disagree the moment the target is edited while a lens is open.
 *
 * Structural input types rather than screens/admin/shared: this is imported by
 * AuthContext, which sits in the entry chunk (see docs/PERF-BUDGETS.md).
 */
import type { ClientFeatures, UserFeatures } from '@parkify/api-types'
import { ALL_PERMISSIONS, getPermissionsForRole, type Permission, type Role } from './permissions'
import type { ViewAsTarget } from './AuthContext'

// A Parkify staffer's own /me answers with this client record (branding + flags
// for the internal org), so a lens on a staffer must too. Mirror of the
// worker's PARKIFY_INTERNAL_CLIENT_ID (api/src/clients/index.ts).
export const PARKIFY_INTERNAL_CLIENT_ID = 'parkify-internal'

export interface ViewAsUserRecord {
  email: string
  name?: string
  role: Role
  clientId: string | null
  features?: UserFeatures | null
}

export interface ViewAsClientRecord {
  id: string
  name: string
  logoUrl?: string
  iconUrl?: string
  accentColor?: string
  features?: ClientFeatures
}

/** The permission matrix as GET /api/admin/permissions returns it. */
export type PermissionMatrix = Partial<Record<string, Role[]>>

/**
 * The target's permissions from the SAME matrix the worker checks them against
 * (Parkify Admin → Roles can customise it). Without a matrix, the built-in
 * defaults — the worker's own fallback when nothing is customised.
 */
export function permissionsFor(role: Role, matrix?: PermissionMatrix | null): Permission[] {
  if (!matrix) return getPermissionsForRole(role)
  return ALL_PERMISSIONS.filter(p => matrix[p]?.includes(role) ?? false)
}

export function buildViewAsTarget(
  user: ViewAsUserRecord,
  clients: ReadonlyMap<string, ViewAsClientRecord>,
  matrix?: PermissionMatrix | null,
): ViewAsTarget {
  const cl = clients.get(user.clientId ?? PARKIFY_INTERNAL_CLIENT_ID)
  return {
    email: user.email,
    name: user.name,
    role: user.role,
    clientId: user.clientId,
    client: cl
      ? {
          id: cl.id, name: cl.name, logoUrl: cl.logoUrl, iconUrl: cl.iconUrl,
          accentColor: cl.accentColor, features: cl.features ?? {},
        }
      : null,
    permissions: permissionsFor(user.role, matrix),
    // The target's OWN nav narrowing (Client Admin → Team). Without it the lens
    // shows every screen their tenant has, including ones hidden from them.
    userFeatures: user.features ?? null,
  }
}
